import {  t  } from "@/client/libs/dummy-i18n";
import { Crown, Sparkle } from "@phosphor-icons/react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@stackmorph/ui";
import { useNavigate } from "react-router";

type UpgradeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  trigger: "template" | "limit" | "feature";
  featureName?: string;
}

export const UpgradeModal = ({ isOpen, onClose, trigger, featureName }: UpgradeModalProps) => {
  const navigate = useNavigate();

  const getContent = () => {
    switch (trigger) {
      case "template": {
        return {
          title: t`Unlock Premium Template`,
          description: t`This template is available exclusively for Premium users. Upgrade now to use this professional design and stand out from the crowd.`,
          icon: <Sparkle size={48} className="text-yellow-500" weight="fill" />,
        };
      }
      case "limit": {
        return {
          title: t`Resume Limit Reached`,
          description: t`You've reached the limit of 3 resumes on the Free plan. Upgrade to Premium for unlimited resumes and more.`,
          icon: <Crown size={48} className="text-yellow-500" weight="fill" />,
        };
      }
      case "feature": {
        return {
          title: t`Unlock Premium Feature`,
          description: t`The ${featureName} feature is available exclusively for Premium users. Upgrade to access powerful tools like this.`,
          icon: <Crown size={48} className="text-yellow-500" weight="fill" />,
        };
      }
      default: {
        return {
          title: t`Upgrade to Premium`,
          description: t`Unlock all features and remove limits by upgrading to Premium.`,
          icon: <Crown size={48} className="text-yellow-500" weight="fill" />,
        };
      }
    }
  };

  const content = getContent();

  const handleUpgrade = () => {
    onClose();
    navigate("/dashboard/premium");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-500/10">
            {content.icon}
          </div>
          <DialogTitle className="text-2xl">{content.title}</DialogTitle>
          <DialogDescription className="mt-2 text-base">
            {content.description}
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 space-y-3 rounded-lg border p-4 bg-secondary/20">
          <div className="flex items-center gap-3">
            <Crown size={20} className="text-yellow-500" />
            <span className="text-sm font-medium">{t`Unlimited Resumes`}</span>
          </div>
          <div className="flex items-center gap-3">
            <Sparkle size={20} className="text-yellow-500" />
            <span className="text-sm font-medium">{t`Access to All Premium Templates`}</span>
          </div>
          <div className="flex items-center gap-3">
            <Sparkle size={20} className="text-yellow-500" />
            <span className="text-sm font-medium">{t`AI Writing Assistant`}</span>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button size="lg" className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:from-yellow-600 hover:to-yellow-700" onClick={handleUpgrade}>
            {t`Upgrade Now`}
          </Button>
          <Button variant="ghost" className="w-full" onClick={onClose}>
            {t`Maybe Later`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
